import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { Router } from '@angular/router'
import { NgxSpinnerService } from 'ngx-spinner'
import { ToastrService } from 'ngx-toastr'
import { AuthService } from '../../services/auth.service'
import { environment } from 'src/config/environments/environment'
import { Country } from '@app/models/country.model'
import { CountriesService } from '@app/shared/pages/settings/services/countries.service'
import { take } from 'rxjs'

@Component({
  selector: 'app-reset-password',
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.scss'],
  standalone: false,
  providers: [CountriesService],
})
export class ResetPasswordComponent implements OnInit {
  form: FormGroup
  formStates = { USE_EMAIL: 'USE_EMAIL', USE_PHONE: 'USE_PHONE' }
  PHONE_SUBTITLE = 'AUTH.APPLY_RESET.PHONE_TITLE'
  EMAIL_SUBTITLE = 'AUTH.APPLY_RESET.EMAIL_TITLE'
  selectedPhoneState = this.formStates.USE_PHONE
  title = 'AUTH.APPLY_RESET.TITLE'
  subTitle = this.PHONE_SUBTITLE

  /** Country-code dropdown options (live data when authenticated, bundled list otherwise) */
  countries: Country[] = []

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private router: Router,
    private countriesService: CountriesService,
  ) {}

  ngOnInit(): void {
    this.initForm()
    this.authService.clearResetData()
    this.loadCountries()
  }

  initForm() {
    this.form = this.fb.group({
      // email: ['', [Validators.required]],
      mobile: ['', [Validators.required]],
      country_code: ['', [Validators.required]],
      user_name: ['', [Validators.required]],
    })
  }

  /**
   * Load the country-code options and preselect the environment default
   * so most users only have to type their number.
   */
  loadCountries() {
    // PAPI stores/matches country_code WITHOUT a leading "+" (e.g. "972"), so compare
    // normalized and submit the list's own value verbatim.
    const stripPlus = (code: string) => (code || '').replace(/^\+/, '')
    const target = stripPlus(environment.defaultCountryCode)

    this.countriesService
      .getCountries()
      .pipe(take(1))
      .subscribe((countries) => {
        this.countries = countries
        if (!this.form.get('country_code')?.value) {
          const defaultCountry = countries.find((c) => stripPlus(c.country_code) === target)
          if (defaultCountry) this.form.get('country_code')?.setValue(defaultCountry.country_code)
        }
      })
  }

  toggleFormState() {
    this.selectedPhoneState = this.selectedPhoneState === this.formStates.USE_EMAIL ? this.formStates.USE_PHONE : this.formStates.USE_EMAIL
    this.subTitle = this.selectedPhoneState === this.formStates.USE_EMAIL ? this.EMAIL_SUBTITLE : this.PHONE_SUBTITLE
  }

  /**
   * we need to check the form based on the selected state
   * so we cant use the form.valid property
   * @description - This method is used to check if the form is valid
   * @returns {boolean} - returns true if the form is valid
   */
  isFormValid() {
    return this.selectedPhoneState === this.formStates.USE_EMAIL ? !!(this.form.value as { email?: string }).email : this.form.valid
  }

  async onResetClick() {
    const formValue = this.form.value as { email: string; mobile: string; country_code: string; user_name: string }
    // here you can have some checks on the form value
    if (this.isFormValid()) {
      this.spinner.show()
      // send request to server
      const body =
        this.selectedPhoneState === this.formStates.USE_EMAIL
          ? { email: formValue.email }
          : {
              mobile: formValue.mobile,
              country_code: formValue.country_code,
              user_name: formValue.user_name,
            }
      this.authService.requestedPasswordReset = true
      this.authService.selectedPhoneState = this.selectedPhoneState
      this.authService.resetBody = body
      try {
        const response = await this.authService.resetPassword().toPromise()
        this.spinner.hide()
        const success = response?.success
        if (success) {
          // Check if we're in dev environment and pin_code is returned
          const pinCode = (response?.data as { pin_code?: string } | undefined)?.pin_code
          if (pinCode && !environment.production) {
            // Auto-fill the pin code and navigate directly to apply-reset
            this.authService.pinCode = pinCode
            this.router.navigate(['/auth/apply-reset'])
          } else {
            this.router.navigate(['/auth/validate-pin'])
          }
        } else {
          this.toastr.error('Phone number or email is not registered')
        }
      } catch (_e) {
        this.spinner.hide()
      }
    } else {
      // show toast error
      this.toastr.error('form is not valid')
    }
  }
}
